import { z } from 'zod';

export const CONTENT_INTELLIGENCE_EVALUATOR = 'content_intelligence' as const;
export const contentIntelligenceEvaluators = [CONTENT_INTELLIGENCE_EVALUATOR] as const;
export const contentIntelligenceEvaluationTriggers = ['scheduled', 'manual'] as const;
export const contentIntelligenceEvaluationModes = ['incremental', 'full'] as const;
export const MAX_CONTENT_INTELLIGENCE_EVALUATION_RUNS = 50;
export const contentIntelligenceEvaluationStatuses = [
  'queued',
  'running',
  'completed',
  'partial',
  'skipped',
  'failed',
] as const;

export type ContentIntelligenceEvaluator = (typeof contentIntelligenceEvaluators)[number];
export type ContentIntelligenceEvaluationTrigger =
  (typeof contentIntelligenceEvaluationTriggers)[number];
export type ContentIntelligenceEvaluationMode = (typeof contentIntelligenceEvaluationModes)[number];
export type ContentIntelligenceEvaluationStatus =
  (typeof contentIntelligenceEvaluationStatuses)[number];

const evaluationCountersSchema = z.object({
  scanned: z.number().int().nonnegative(),
  modelCalls: z.number().int().nonnegative(),
  assessed: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
  sourceUnavailable: z.number().int().nonnegative(),
  unparseable: z.number().int().nonnegative(),
  retryableErrors: z.number().int().nonnegative(),
  retryExhausted: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});

export const contentIntelligenceEvaluationRunSchema = z.object({
  id: z.string().min(1),
  loopId: z.number().int().positive(),
  evaluator: z.enum(contentIntelligenceEvaluators),
  trigger: z.enum(contentIntelligenceEvaluationTriggers),
  mode: z.enum(contentIntelligenceEvaluationModes),
  status: z.enum(contentIntelligenceEvaluationStatuses),
  requestedByUserId: z.string().nullable(),
  pipelineVersion: z.string().nullable(),
  counters: evaluationCountersSchema,
  completedScan: z.boolean(),
  sourceChanges: z.boolean().nullable(),
  error: z.string().nullable(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const contentIntelligenceEvaluationRunsQuerySchema = z.object({
  loopId: z.coerce.number().int().positive(),
  limit: z.coerce.number().int().min(1).max(MAX_CONTENT_INTELLIGENCE_EVALUATION_RUNS).default(20),
});

export const contentIntelligenceEvaluationRunsResponseSchema = z.object({
  latest: contentIntelligenceEvaluationRunSchema.nullable(),
  runs: z
    .array(contentIntelligenceEvaluationRunSchema)
    .max(MAX_CONTENT_INTELLIGENCE_EVALUATION_RUNS),
});

export const contentIntelligenceEvaluationSyncRequestSchema = z
  .object({
    loopId: z.number().int().positive(),
    mode: z.enum(contentIntelligenceEvaluationModes).default('incremental'),
  })
  .strict();

export const contentIntelligenceEvaluationSyncResponseSchema = z.object({
  run: contentIntelligenceEvaluationRunSchema,
});

export type ContentIntelligenceEvaluationRunResponse = z.infer<
  typeof contentIntelligenceEvaluationRunSchema
>;
export type ContentIntelligenceEvaluationRunsResponse = z.infer<
  typeof contentIntelligenceEvaluationRunsResponseSchema
>;
export type ContentIntelligenceEvaluationSyncRequest = z.infer<
  typeof contentIntelligenceEvaluationSyncRequestSchema
>;
export type ContentIntelligenceEvaluationSyncResponse = z.infer<
  typeof contentIntelligenceEvaluationSyncResponseSchema
>;
